import { Input } from 'antd';
import React, { useContext, useState } from 'react';
import { Level, OptimisationPlanData } from '../OptimizePlan';
import { UserContext } from 'contexts/ConfigContext';
import { formatNumWithKMBAlt, getConfig } from 'utils/helperMethods';
import _, { isNil } from 'lodash';
import RangeInputWithPopover from 'components/molecules/common/RangeInpuWithPopover';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
interface IProps {
    rowData: OptimisationPlanData;
    setTableData?: React.Dispatch<React.SetStateAction<OptimisationPlanData[]>>;
    index: number;
    level: string;
    tableColumns: ITableColumn[];
    expandRows?: boolean;
}
interface ITableColumn {
    key: string;
    type: InputTypes;
    isEditable: boolean;
    className?: string;
    rangeInputKeys?: string[];
    highlightText?: boolean;
}

export enum InputTypes {
    Range = 'range',
    Text = 'text',
}

const OptimizeTableContent: React.FC<IProps> = ({
    rowData,
    setTableData = () => {},
    index,
    level,
    tableColumns,
    expandRows = false,
}) => {
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const { decimalForPercentage, decimalForNonPercentage } = commonConfig ?? {};

    const [expand, setExpand] = useState(expandRows);
    const { levelDetails: nestedData, ...restRowData } = rowData;

    const isPercentage = restRowData.label?.includes('%');
    const decimalType = isPercentage ? decimalForPercentage : decimalForNonPercentage;

    const handleExpand = () => setExpand(!expand);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setTableData((prev) => {
            prev[index].single_value = Number(value);
            return [...prev];
        });
    };

    const handleRangeChange = (value: number | null, min: boolean, isNested = false, rowIndex: number) => {
        setTableData((prev) => {
            const target = isNested ? prev[index].levelDetails?.[rowIndex] : prev[index];
            if (target) {
                if (min) target.min_value = Number(value);
                else target.max_value = Number(value);
            }
            return [...prev];
        });
    };

    const getInputComponent = (
        inputType: InputTypes,
        name: string,
        minVal: number | null,
        maxVal: number | null,
        rowIndex: number,
        isNested = false,
        defaultValue?: number | null,
    ) => {
        switch (inputType) {
            case InputTypes.Range:
                return (
                    <RangeInputWithPopover
                        maxVal={maxVal}
                        minVal={minVal}
                        onMaxChange={(val) => handleRangeChange(val, false, isNested, rowIndex)}
                        onMinChange={(val) => handleRangeChange(val, true, isNested, rowIndex)}
                    />
                );
            case InputTypes.Text:
                return (
                    <Input
                        className="!border !border-[rgba(217, 217, 217, 1)] !rounded-md !w-40"
                        onChange={handleInputChange}
                        name={name}
                        value={defaultValue ?? ''}
                        type="number"
                    />
                );
            default:
                return null;
        }
    };

    const getCellValue = (key: string, data: OptimisationPlanData, type: InputTypes, rangeInputKeys?: string[]) => {
        const dataValue = data[key as keyof OptimisationPlanData];

        if (type === InputTypes.Range) {
            const minValue = data[rangeInputKeys?.[0] as keyof OptimisationPlanData];
            const maxValue = data[rangeInputKeys?.[1] as keyof OptimisationPlanData];

            if (isNil(minValue) || isNil(maxValue)) return '-';

            return `${formatNumWithKMBAlt(Number(minValue), decimalType)} - ${formatNumWithKMBAlt(
                Number(maxValue),
                decimalType,
            )}`;
        }

        if (dataValue) {
            return isNaN(Number(dataValue))
                ? (dataValue as string)
                : formatNumWithKMBAlt(Number(dataValue), decimalType);
        }
        return '-';
    };

    const isObjectiveConstraint = rowData?.objectiveConstraint ?? false;

    return (
        <>
            {/* Top-level row */}
            <div className="flex justify-between gap-4 border-b border-[rgba(240,240,240,1)] last:border-none items-center bg-white">
                {tableColumns.map((column, colIndex) => {
                    const { key, type, className, isEditable, rangeInputKeys, highlightText } = column;
                    const displayValue = getCellValue(key, rowData, type, rangeInputKeys);
                    const toHighlight = level === 'category' ? highlightText : false;
                    const value = rowData[key as keyof OptimisationPlanData];

                    if (isEditable) {
                        return (
                            <div key={colIndex} className={`flex-1 p-4 ${className ?? ''}`}>
                                {level === 'category'
                                    ? getInputComponent(
                                          type,
                                          restRowData.name,
                                          Number(rowData[rangeInputKeys?.[0] as keyof OptimisationPlanData]) ?? null,
                                          Number(rowData[rangeInputKeys?.[1] as keyof OptimisationPlanData]) ?? null,
                                          index,
                                          false,
                                          Number(rowData[key as keyof OptimisationPlanData]) ?? null,
                                      )
                                    : '-'}
                            </div>
                        );
                    }

                    return (
                        <span
                            className={`flex-1 p-4 ${className ?? ''} ${
                                toHighlight ? (Number(value) > 0 ? 'text-successGreen' : 'text-redAlt') : ''
                            }`}
                        >
                            {/* No need to show value for objective constraint */}
                            {isObjectiveConstraint && column.key === '' ? '' : displayValue}
                        </span>
                    );
                })}

                {level !== 'category' && (
                    <div className="w-12 h-full flex items-center justify-center">
                        {expand ? <UpOutlined onClick={handleExpand} /> : <DownOutlined onClick={handleExpand} />}
                    </div>
                )}
            </div>

            {expand &&
                nestedData?.map((item, nestedIndex) => (
                    <div
                        key={nestedIndex}
                        className="flex justify-between gap-4 border-b border-[rgba(240,240,240,1)] last:border-none items-center bg-backgroundGreyAlt"
                    >
                        {tableColumns.map((column, colIndex) => {
                            const { key, type, isEditable, rangeInputKeys, className, highlightText } = column;
                            const displayValue = getCellValue(key, item, type, rangeInputKeys);
                            const value = item[key as keyof OptimisationPlanData];

                            if (isEditable) {
                                return (
                                    <div key={colIndex} className={`flex-1 p-4 ${className ?? ''}`}>
                                        {getInputComponent(
                                            type,
                                            item.name,
                                            Number(item[rangeInputKeys?.[0] as keyof OptimisationPlanData]),
                                            Number(item[rangeInputKeys?.[1] as keyof OptimisationPlanData]),
                                            nestedIndex,
                                            true,
                                            Number(item[key as keyof OptimisationPlanData]) ?? null,
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={colIndex}
                                    className={`flex-1 p-4 ${className ?? ''} ${
                                        highlightText ? (Number(value) > 0 ? 'text-successGreen' : 'text-redAlt') : ''
                                    }`}
                                >
                                    {isObjectiveConstraint && column.key === '' ? '' : displayValue}
                                </div>
                            );
                        })}

                        <div className="w-12 h-full flex items-center justify-center" />
                    </div>
                ))}
        </>
    );
};

export default OptimizeTableContent;
